import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  alpha,
  FormControl,
  InputBase,
  InputLabel,
  styled,
  Typography,
  Grid,
  InputAdornment,
  TextField,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { sendSMSEmail, confirmOTPEmail } from "../../api/auth";
import { useSnackbarStore } from "../../store/useSnackbarStore";
import { useConfirmEmail } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

export interface LoginMailForm {
  email: string;
  otp: string;
}

export default function ModalLoginMailForm({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [form, setForm] = useState<LoginMailForm>({ email: "", otp: "" });
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log("submitted:", form);
    onClose();
  };
  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const [countdown, setCountdown] = useState(0);
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const { mutate: confirm, isPending: isGenerating } = useConfirmEmail();

  const handleGetCode = async () => {
    try {
      // call API
      setCountdown(30);
      const response = await sendSMSEmail(form.email);
      console.log("API success:", response);
    } catch (error) {
      console.error("API error:", error);
      // optionally show an error snackbar/dialog
    }
  };

  const handleConfirmEmail = async () => {
    confirm(
      { email: form.email, otp: form.otp },
      {
        onSuccess: (data: any) => {
          onClose();
          navigate(
            `/auth/callback?email=${data?.email}&name=${data?.name}&picture=${data?.picture}&token=${data?.token}&cus_id=${data?.cus_id}`
          );
        },
        onError: (err: any) => {
          console.error("💥 Active eSim failed:", err);
          showSnackbar(`${err?.response?.data?.message || ""}`, "warning");
        },
      }
    );
  };

  // countdown effect
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [countdown]);

  const BootstrapInput = styled(InputBase)(({ theme }) => ({
    "label + &": {
      marginTop: theme.spacing(3),
    },
    "& .MuiInputBase-input": {
      borderRadius: 4,
      position: "relative",
      backgroundColor: "#F3F6F9",
      border: "1px solid",
      borderColor: "#E0E3E7",
      fontSize: 16,
      padding: "10px 12px",
      transition: theme.transitions.create([
        "border-color",
        "background-color",
        "box-shadow",
      ]),
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(","),
      "&:focus": {
        boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
      ...theme.applyStyles("dark", {
        backgroundColor: "#1A2027",
        borderColor: "#2D3843",
      }),
    },
  }));

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          onClose();
        }}
        fullScreen={fullScreen} // 👈 dynamic fullscreen
        PaperProps={{
          sx: !fullScreen
            ? {
                width: "50vw",
                maxWidth: "50vw",
              }
            : {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
        }}
        disableEscapeKeyDown
      >
        <form onSubmit={handleSubmit} id="subscription-form">
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              Login via email
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              {/* Email */}
              <Grid item xs={12}>
                <FormControl variant="standard" fullWidth>
                  <TextField
                    fullWidth
                    label="Email"
                    value={form.email}
                    margin="dense"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    required
                    placeholder="Enter your email and get code"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                </FormControl>
              </Grid>

              {/* SMS Code */}
              <Grid item xs={12}>
                <FormControl variant="standard" fullWidth>
                  <FormControl variant="standard" fullWidth>
                    <TextField
                      fullWidth
                      label="SMS code"
                      value={form.otp}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setForm((prev) => ({ ...prev, otp: e.target.value }))
                      }
                      required
                      placeholder="Enter your code"
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              variant="text"
                              disabled={
                                !isValidEmail(form.email) || !!countdown
                              }
                              onClick={handleGetCode}
                            >
                              {countdown
                                ? `Resend (${countdown}s)`
                                : "Get code"}
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions style={{ marginRight: "18px", marginBottom: "20px" }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirmEmail}
              size="large"
              disabled={isGenerating}
            >
              {isGenerating ? <CircularProgress size={26} /> : "Login"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
}
